
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBackToDashboard } from "../../utils/navigationUtils";
import {
  User,
  FileText,
  Upload,
  X,
  Plus,
  Trash2,
  CheckCircle,
  AlertCircle,
  Calendar,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Stethoscope,
  Building,
  Users,
  Heart,
  ArrowLeft,
  ArrowRight,
  Save,
  Eye,
  EyeOff,
  AlertTriangle,
  RefreshCw,
  Clock,
  History,
  Info
} from "lucide-react";
import { createPatientApplication } from "../../services/applicationService";
import { getApprovedLeadGenerators } from "../../services/leadGeneratorService";
import { getDoc, doc } from "firebase/firestore";
import { db } from "../../services/firebaseConfig";
import { useAuth } from "../../context/AuthContext";
import { useSearchParams } from "react-router-dom";

const NewPatientApplicationLead = ({ onBack }) => {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const goBackToDashboard = useBackToDashboard();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [applicationId, setApplicationId] = useState("");
  const [caseId, setCaseId] = useState("");
  const [searchParams] = useSearchParams();

  const { userData } = useAuth();
  const hospitalId = searchParams.get("hospitalId") || ""; // Get hospitalId from URL parameters
  const leadGeneratorId = searchParams.get("leadGeneratorId") || userData?.leadGeneratorId || ""; // Get leadGeneratorId from URL parameters
  const isSuperAdmin = userData?.role === 'superadmin'; // Assuming role is available in userData

  const [formData, setFormData] = useState({
    // Patient Information
    patientName: "",
    patientAge: "",
    patientGender: "",
    patientAddress: "",
    patientPhone: "",
    patientEmail: "",

    // Patient KYC
    patientAadhar: "",
    patientPan: "",
    patientAddressProof: "",

    // Policy Holder Info
    isSelf: false, // Default unchecked; user selects if patient is also the policy holder
    policyHolderName: "",
    policyHolderAge: "",
    policyHolderGender: "",
    policyHolderAddress: "",
    policyHolderPhone: "",
    policyHolderEmail: "",
  relationshipWithPatient: "Self",
  // New mandatory fields for Policy Holder
  policyHolderDob: "",
  policyHolderAlternatePhone: "",

    // Policy Holder KYC
    policyHolderAadhar: "",
    policyHolderPan: "",
    policyHolderAddressProof: "",

    // Nominee Information
    nomineeName: "",
    relationshipWithNominee: "",
    nomineeContact: "",

    // Policy Information (moved before diagnosis)
    policyType: "",
  corporateName: "",
    policyNumber: "",
    policyStartDate: "",
    policyEndDate: "",
    insuranceCompany: "",
    tpaName: "",

    // Employment Information
    employmentStatus: "",
    workingCompanyName: "",
    monthlyIncome: "",
    designation: "",
    natureOfWork: "",

    // Medical History
    hasMedicalHistory: false,
    medicalHistory: "",
    medicalDiagnosis: "",
    medicalDuration: "",
    surgicalHistory: "",
    surgicalDiagnosis: "",
    surgicalDuration: "",
    surgicalProcedure: "",

    // Diagnosis Details
    treatmentType: "medical",
    doctorName: "",
    doctorDesignation: "",
    admissionDate: "",
    dischargeDate: "",
    estimatedCost: "",
    // actualCost removed as requested

    // Additional Information moved to diagnosis section
    remarks: "",
    priority: "normal",
    emergency: false,
    treatmentPlanned: "planned", // emergency or planned
  });

  const [treatments, setTreatments] = useState([]);
  const [newTreatment, setNewTreatment] = useState({
    diagnosisName: "",
    treatmentType: "",
    procedureName: "",
    description: "",
  });

  // Auto-save functionality
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load saved data on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('newPatientApplication');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        const confirmContinue = window.confirm(
          'Found an incomplete application. Would you like to continue from where you left off or start a new application?'
        );

        if (confirmContinue) {
          setFormData(parsedData.formData || formData);
          setTreatments(parsedData.treatments || []);
          setCurrentStep(parsedData.currentStep || 1);
        } else {
          localStorage.removeItem('newPatientApplication');
        }
      } catch (error) {
        console.error('Error loading saved data:', error);
        localStorage.removeItem('newPatientApplication');
      }
    }
  }, []);

  // Auto-save data whenever formData or treatments change
  useEffect(() => {
    if (hasUnsavedChanges) {
      // Clean formData to avoid undefined values in localStorage
      const cleanedFormData = Object.fromEntries(
        Object.entries(formData).map(([key, value]) => [
          key,
          value === undefined ? "" : value
        ])
      );

      const saveData = {
        formData: cleanedFormData,
        treatments,
        currentStep,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('newPatientApplication', JSON.stringify(saveData));
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
    }
  }, [formData, treatments, currentStep, hasUnsavedChanges]);

  // Trigger auto-save when data changes
  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [formData, treatments]);

  // Fetch lead generator on component mount
  useEffect(() => {
    fetchLeadGenerator();
  }, [leadGeneratorId]);

  // Function to fetch the specific lead generator by ID
  const fetchLeadGenerator = async () => {
    if (!leadGeneratorId) {
      console.error("No lead generator ID provided");
      setLoadingLeadGenerators(false);
      return;
    }

    setLoadingLeadGenerators(true);
    try {
      // Get lead generator details directly from leadGeneratorApplications collection using the ID
      const leadGenDocRef = doc(db, "leadGeneratorApplications", leadGeneratorId);
      const leadGenDoc = await getDoc(leadGenDocRef);

      if (leadGenDoc.exists()) {
        const leadGenData = leadGenDoc.data();

        // Only proceed if lead generator is approved and active
        if (leadGenData.status === 'approved' && leadGenData.isActive) {
          const leadGen = {
            id: leadGenDoc.id,
            entityName: leadGenData.entityName || "",
            city: leadGenData.city || "",
            state: leadGenData.state || "",
            servicesRequired: leadGenData.servicesRequired || [],
            cinNumber: leadGenData.cinNumber || "",
            leadGeneratorId: leadGenData.leadGeneratorId || leadGenDoc.id,
          };

          setSelectedLeadGenerator(leadGen);
          console.log("Lead generator loaded:", leadGen);
        } else {
          console.warn("Lead generator is not approved or not active");
          setSelectedLeadGenerator(null);
        }
      } else {
        console.error("Lead generator not found");
        setSelectedLeadGenerator(null);
      }
    } catch (error) {
      console.error("Error fetching lead generator:", error);
      setSelectedLeadGenerator(null);
    } finally {
      setLoadingLeadGenerators(false);
    }
  };

  const [documents, setDocuments] = useState({
  // Patient KYC Documents
  patientAadharDoc: null, // legacy combined (kept for backward compatibility)
  patientAadharFrontDoc: null,
  patientAadharBackDoc: null,
  patientPanDoc: null,
  patientAddressProofDoc: null,

  // Policy Holder KYC Documents
  policyHolderAadharDoc: null, // legacy combined
  policyHolderAadharFrontDoc: null,
  policyHolderAadharBackDoc: null,
  policyHolderPanDoc: null,
  policyHolderAddressProofDoc: null,
  policyHolderChequeOrStatementDoc: null,
  patientChequeOrStatementDoc: null,

  // Policy Documents (multi)
  policyDocument: null, // legacy single
  patientPolicyCopyDocs: [],
  policyHolderPolicyCopyDocs: [],

  // Treatment Documents
  treatmentDocs: {},

  // Medical History Documents (multiple uploads)
  medicalHistoryDocs: [],
  medicalDocs: [],
  investigationReports: [],
  });

  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  const [showAdmissionDateOverride, setShowAdmissionDateOverride] = useState(false);

  // Lead Generator state
  const [selectedLeadGenerator, setSelectedLeadGenerator] = useState(null);
  const [loadingLeadGenerators, setLoadingLeadGenerators] = useState(false);

  const steps = [
    { id: 1, title: "Patient Information", icon: User, description: "Basic patient details" },
    { id: 2, title: "Policy Holder Info", icon: Users, description: "Policy holder, nominee, and employment" },
    { id: 3, title: "KYC Details", icon: FileText, description: "Provide KYC numbers only" },
    { id: 4, title: "Medical History", icon: History, description: "Patient medical background" },
    { id: 5, title: "Policy Information", icon: CreditCard, description: "Insurance policy details" },
    { id: 6, title: "Diagnosis Details", icon: Stethoscope, description: "Medical diagnosis and treatment" },
    { id: 7, title: "Upload Documents", icon: Upload, description: "Upload all required files" },
    { id: 8, title: "Review & Submit", icon: CheckCircle, description: "Final review and submission" },
  ];

  const genderOptions = ["Male", "Female", "Other"];
  const treatmentTypes = ["medical", "surgical"];
  const relationshipOptions = [
    "Self",
    "Spouse",
    "Father",
    "Mother",
    "Son",
    "Daughter",
    "Brother",
    "Sister",
    "Father-in-law",
    "Mother-in-law",
    "Other",
  ];
  const insuranceCompanies = [
    "Bajaj Allianz General Insurance Co. Ltd",
    "Cholamandalam MS General Insurance Co. Ltd",
    "Navi General Insurance Ltd.",
    "Future Generali India Insurance Co. Ltd.",
    "HDFC ERGO General Insurance Co. Ltd",
    "IFFCO-TOKIO General Insurance Co. Ltd.",
    "ICICI Lombard General Insurance Co. Ltd",
    "Kotak Mahindra General insurance co. Ltd.",
    "Liberty General Insurance Co. Ltd.",
    "Magma HDI General Insurance Co. Ltd.",
    "National Insurance Co. Ltd.",
    "Raheja QBE General Insurance Co. Ltd.",
    "Reliance General Insurance Co. Ltd.",
    "Royal Sundaram General Insurance Co. Ltd",
    "SBI General Insurance Co. Ltd.",
    "Shriram General Insurance Co. Ltd.",
    "Tata-AIG General Insurance Co. Ltd.",
    "The New India Assurance Co. Ltd.",
    "The Oriental Insurance Co. Ltd.",
    "Universal Sompo General Insurance Co. Ltd.",
    "United India Insurance Co. Ltd.",
    "Acko General Insurance Ltd.",
    "Edelweiss General Insurance Company Limited",
    "Go Digit General Insurance Limited",
    "Aditya Birla Health insurance Co Ltd.",
    "Manipal Cigna Health Insurance Company Limited",
    "Niva bupa health insurance company limited",
    "Care Health Insurance Ltd",
    "Star Health & Allied Insurance Co. Ltd",
  ];

  const tpaCompanies = [
    "In-House",
    "Medi Assist Insurance TPA Private Limited",
    "MDIndia Health Insurance TPA Private Limited",
    "Paramount Health Services & Insurance TPA Private Limited",
    "Heritage Health Insurance TPA Private Limited",
    "Family Health Plan Insurance TPA Limited",
    "Raksha Health Insurance TPA Private Limited",
    "Vidal Health Insurance TPA Private Limited",
    "Volo Health Insurance TPA Pvt. Ltd ( Formerly East West Assist Insurance TPA Private Limited)",
    "Medsave Health Insurance TPA Limited",
    "Genins India Insurance TPA Limited",
    "Health India Insurance TPA Services Private Limited",
    "Good Health Insurance TPA Limited",
    "Park Mediclaim Insurance TPA Private Limited",
    "Safeway Insurance TPA Private Limited",
    "Ericson Insurance TPA Private Limited",
    "Health Insurance TPA of India Limited",
    "Vision Digital Insurance TPA Private Limited",
    "Link-K Insurance TPA Private Limited",
    "AKNA Health Insurance TPA Private Limited",
  ];

  // Helper function to check if admission date is within 3 days
  const isAdmissionDateValid = (date) => {
    if (!date) return false;
    const admissionDate = new Date(date);
    const currentDate = new Date();
    const threeDaysAgo = new Date(currentDate.getTime() - 3 * 24 * 60 * 60 * 1000);
    return admissionDate >= threeDaysAgo;
  };

  // Helper function to check if admission date is within policy period
  const isAdmissionWithinPolicyPeriod = (admissionDate, policyStartDate, policyEndDate) => {
    if (!admissionDate || !policyStartDate || !policyEndDate) return false;
    const admission = new Date(admissionDate);
    const policyStart = new Date(policyStartDate);
    const policyEnd = new Date(policyEndDate);
    return admission >= policyStart && admission <= policyEnd;
  };

  // Helper function to validate policy duration (should be whole years)
  const validatePolicyDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return { valid: false, message: "" };

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end <= start) {
      return { valid: false, message: "End date must be after start date" };
    }

    // Calculate the difference in days
    const diffTime = end.getTime() - start.getTime();
    const diffDays = diffTime / (1000 * 3600 * 24);

    // Check if it's exactly whole years (365 days per year)
    const years = Math.round(diffDays / 365);
    const expectedDays = years * 365;

    if (Math.abs(diffDays - expectedDays) > 1) {
      return {
        valid: false,
        message: "Policy duration must be in whole years only (1 year, 2 years, etc.)"
      };
    }

    return {
      valid: true,
      message: years > 1 ? `Policy duration: ${years} years` : `Policy duration: ${years} year`,
      years: years
    };
  };

  // Helper function to check policy expiry status
  const checkPolicyExpiry = (endDate) => {
    if (!endDate) return { status: "unknown", message: "" };

    const end = new Date(endDate);
    const today = new Date();
  const fifteenDaysFromNow = new Date(today.getTime() + 15 * 24 * 60 * 60 * 1000);

    if (end < today) {
      return {
        status: "expired",
        message: "Policy has expired. Have you renewed it or is renewal in process?"
      };
    } else if (end <= fifteenDaysFromNow) {
      return {
        status: "expiring",
        message: "Policy is expiring within 15 days. Please check renewal status."
      };
    }

    return { status: "active", message: "" };
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = value;

    // Normalize PAN fields: uppercase and alphanumeric only
    if (name.includes("Pan") && type !== "checkbox") {
      processedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    }

    // Normalize policy number to uppercase
    if (name === "policyNumber" && type !== "checkbox") {
      processedValue = value.toUpperCase();
    }

    // Clamp Policy Holder DOB to not be in the future
    if (name === "policyHolderDob" && type !== "checkbox") {
      if (processedValue) {
        const today = new Date();
        const inputDate = new Date(processedValue);
        if (inputDate > today) {
          const yyyy = today.getFullYear();
          const mm = String(today.getMonth() + 1).padStart(2, "0");
          const dd = String(today.getDate()).padStart(2, "0");
          processedValue = `${yyyy}-${mm}-${dd}`;
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : processedValue,
    }));

    // Auto-populate policy holder data if self
    if (name === "isSelf") {
      if (checked) {
        setFormData((prev) => ({
          ...prev,
          policyHolderName: prev.patientName,
          policyHolderAge: prev.patientAge,
          policyHolderGender: prev.patientGender,
          policyHolderAddress: prev.patientAddress,
          policyHolderPhone: prev.patientPhone,
          policyHolderEmail: prev.patientEmail,
          policyHolderAadhar: prev.patientAadhar,
          policyHolderPan: prev.patientPan,
          policyHolderAddressProof: prev.patientAddressProof,
          relationshipWithPatient: "Self",
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          policyHolderName: "",
          policyHolderAge: "",
          policyHolderGender: "",
          policyHolderAddress: "",
          policyHolderPhone: "",
          policyHolderEmail: "",
          policyHolderAadhar: "",
          policyHolderPan: "",
          policyHolderAddressProof: "",
          relationshipWithPatient: "",
        }));
      }
    }

    // Clear medical history fields when checkbox is unchecked
    if (name === "hasMedicalHistory") {
      if (!checked) {
        setFormData((prev) => ({
          ...prev,
          medicalHistory: "",
          medicalDiagnosis: "",
          medicalDuration: "",
          surgicalHistory: "",
          surgicalDiagnosis: "",
          surgicalDuration: "",
          surgicalProcedure: "",
        }));
        // Clear medical history documents
        setDocuments((prev) => ({
          ...prev,
          medicalHistoryDocs: [],
        }));
      }
    }

    // Clear employment fields when policy type changes
    if (name === "policyType") {
      setFormData((prev) => ({
        ...prev,
        workingCompanyName: "",
        designation: "",
        natureOfWork: "",
      }));
    }

    // Clear or retain fields when employment status changes
    if (name === "employmentStatus") {
      setFormData((prev) => ({
        ...prev,
        designation: value === "employed" ? prev.designation : "",
        workingCompanyName: value === "employed" ? prev.workingCompanyName : "",
        natureOfWork: value === "self-employed" ? prev.natureOfWork : "",
      }));
    }

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileUpload = (docType, file) => {
    if (!file) return;
    // Allow larger files (up to 100MB) for cheque/bank statement uploads
    const isChequeOrStatement =
      docType === "policyHolderChequeOrStatementDoc" ||
      docType === "patientChequeOrStatementDoc";
    const maxSize = isChequeOrStatement ? 100 * 1024 * 1024 : 5 * 1024 * 1024; // 100MB for cheque/statement, 5MB otherwise
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];

    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        [docType]: `File size should be less than ${isChequeOrStatement ? "100MB" : "5MB"}`,
      }));
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        [docType]: "Please upload PDF, JPEG, or PNG files only",
      }));
      return;
    }

    setDocuments((prev) => ({ ...prev, [docType]: file }));
    setErrors((prev) => ({ ...prev, [docType]: "" }));

    // Simulate upload progress
    setUploadProgress((prev) => ({ ...prev, [docType]: 0 }));
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        const progress = (prev[docType] || 0) + 10;
        if (progress >= 100) {
          clearInterval(interval);
          return { ...prev, [docType]: 100 };
        }
        return { ...prev, [docType]: progress };
      });
    }, 100);
  };

  const handleMedicalDocsUpload = (files) => {
    if (!files || files.length === 0) return;
    const maxSize = 100 * 1024 * 1024; // 100MB
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
    const validFiles = [];
    const fileErrors = [];
    Array.from(files).forEach((file) => {
      if (file.size > maxSize) fileErrors.push(`File ${file.name}: Size should be less than 100MB`);
      else if (!allowedTypes.includes(file.type)) fileErrors.push(`File ${file.name}: Please upload PDF, JPEG, or PNG files only`);
      else validFiles.push(file);
    });
    if (fileErrors.length > 0) {
      setErrors((prev) => ({ ...prev, medicalDocs: fileErrors.join(', ') }));
      return;
    }
    setDocuments((prev) => ({ ...prev, medicalDocs: [...prev.medicalDocs, ...validFiles] }));
    setErrors((prev) => ({ ...prev, medicalDocs: "" }));
  };

  const handleInvestigationReportsUpload = (files) => {
    if (!files || files.length === 0) return;
    const maxSize = 100 * 1024 * 1024; // 100MB
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
    const validFiles = [];
    const fileErrors = [];
    Array.from(files).forEach((file) => {
      if (file.size > maxSize) fileErrors.push(`File ${file.name}: Size should be less than 100MB`);
      else if (!allowedTypes.includes(file.type)) fileErrors.push(`File ${file.name}: Please upload PDF, JPEG, or PNG files only`);
      else validFiles.push(file);
    });
    if (fileErrors.length > 0) {
      setErrors((prev) => ({ ...prev, investigationReports: fileErrors.join(', ') }));
      return;
    }
    setDocuments((prev) => ({ ...prev, investigationReports: [...prev.investigationReports, ...validFiles] }));
    setErrors((prev) => ({ ...prev, investigationReports: "" }));
  };

  const handlePatientPolicyCopyUpload = (files) => {
    if (!files || files.length === 0) return;
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
    const validFiles = [];
    const fileErrors = [];
    Array.from(files).forEach((file) => {
      if (file.size > maxSize) fileErrors.push(`File ${file.name}: Size should be less than 5MB`);
      else if (!allowedTypes.includes(file.type)) fileErrors.push(`File ${file.name}: Please upload PDF, JPEG, or PNG files only`);
      else validFiles.push(file);
    });
    if (fileErrors.length > 0) {
      setErrors((prev) => ({ ...prev, patientPolicyCopyDocs: fileErrors.join(', ') }));
      return;
    }
    setDocuments((prev) => ({ ...prev, patientPolicyCopyDocs: [...(prev.patientPolicyCopyDocs || []), ...validFiles] }));
    setErrors((prev) => ({ ...prev, patientPolicyCopyDocs: "" }));
  };

  const handlePolicyHolderPolicyCopyUpload = (files) => {
    if (!files || files.length === 0) return;
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
    const validFiles = [];
    const fileErrors = [];
    Array.from(files).forEach((file) => {
      if (file.size > maxSize) fileErrors.push(`File ${file.name}: Size should be less than 5MB`);
      else if (!allowedTypes.includes(file.type)) fileErrors.push(`File ${file.name}: Please upload PDF, JPEG, or PNG files only`);
      else validFiles.push(file);
    });
    if (fileErrors.length > 0) {
      setErrors((prev) => ({ ...prev, policyHolderPolicyCopyDocs: fileErrors.join(', ') }));
      return;
    }
    setDocuments((prev) => ({ ...prev, policyHolderPolicyCopyDocs: [...(prev.policyHolderPolicyCopyDocs || []), ...validFiles] }));
    setErrors((prev) => ({ ...prev, policyHolderPolicyCopyDocs: "" }));
  };

  // Handle multiple medical history document uploads
  const handleMedicalHistoryDocUpload = (files) => {
    if (!files || files.length === 0) return;
    const maxSize = 100 * 1024 * 1024; // 100MB per file
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];

    const validFiles = [];
    const fileErrors = [];

    Array.from(files).forEach((file, index) => {
      if (file.size > maxSize) {
        fileErrors.push(`File ${file.name}: Size should be less than 100MB`);
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        fileErrors.push(`File ${file.name}: Please upload PDF, JPEG, or PNG files only`);
        return;
      }

      validFiles.push(file);
    });

    if (fileErrors.length > 0) {
      setErrors((prev) => ({
        ...prev,
        medicalHistoryDocs: fileErrors.join(', '),
      }));
      return;
    }

    setDocuments((prev) => ({
      ...prev,
      medicalHistoryDocs: [...prev.medicalHistoryDocs, ...validFiles],
    }));
    setErrors((prev) => ({ ...prev, medicalHistoryDocs: "" }));
  };

  const removeMedicalHistoryDoc = (index) => {
    setDocuments((prev) => ({
      ...prev,
      medicalHistoryDocs: prev.medicalHistoryDocs.filter((_, i) => i !== index),
    }));
  };

  const removeFile = (docType) => {
    setDocuments((prev) => ({ ...prev, [docType]: null }));
    setUploadProgress((prev) => ({ ...prev, [docType]: 0 }));
  };

  const addTreatment = () => {
    if (!newTreatment.diagnosisName.trim()) {
      setErrors((prev) => ({
        ...prev,
        diagnosisName: "Diagnosis name is required",
      }));
      return;
    }

    const treatment = {
      id: Date.now(),
      ...newTreatment,
    };

    setTreatments((prev) => [...prev, treatment]);
    setNewTreatment({
      diagnosisName: "",
      treatmentType: "",
      procedureName: "",
      description: "",
    });
    setErrors((prev) => ({ ...prev, diagnosisName: "" }));
  };

  const removeTreatment = (treatmentId) => {
    setTreatments((prev) => prev.filter((t) => t.id !== treatmentId));
    // Also remove associated documents
    setDocuments((prev) => {
      const newDocs = { ...prev };
      delete newDocs.treatmentDocs[treatmentId];
      return newDocs;
    });
  };

  const handleTreatmentDocUpload = (treatmentId, file) => {
    if (!file) return;
    const maxSize = 5 * 1024 * 1024; // 5MB (unchanged for treatment docs)
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];

    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
  [`treatmentDoc_${treatmentId}`]: "File size should be less than 5MB",
      }));
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        [`treatmentDoc_${treatmentId}`]:
          "Please upload PDF, JPEG, or PNG files only",
      }));
      return;
    }

    setDocuments((prev) => ({
      ...prev,
      treatmentDocs: {
        ...prev.treatmentDocs,
        [treatmentId]: file,
      },
    }));

    setErrors((prev) => ({ ...prev, [`treatmentDoc_${treatmentId}`]: "" }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1: // Patient Information
        if (!formData.patientName.trim())
          newErrors.patientName = "Patient name is required";
        if (!formData.patientAge)
          newErrors.patientAge = "Patient age is required";
        else if (
          parseInt(formData.patientAge) < 0 ||
          parseInt(formData.patientAge) > 120
        )
          newErrors.patientAge = "Invalid age";
        if (!formData.patientGender)
          newErrors.patientGender = "Patient gender is required";
        if (!formData.patientAddress.trim())
          newErrors.patientAddress = "Patient address is required";
        if (!formData.patientPhone.trim())
          newErrors.patientPhone = "Patient phone is required";
        else if (
          !/^[6-9]\d{9}$/.test(formData.patientPhone.replace(/\s+/g, ""))
        )
          newErrors.patientPhone = "Invalid phone number";
        if (
          formData.patientEmail &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.patientEmail)
        )
          newErrors.patientEmail = "Invalid email format";
        break;
  case 2: // Policy Holder Info + Employment
        if (!formData.isSelf) {
          if (!formData.policyHolderName.trim())
            newErrors.policyHolderName = "Policy holder name is required";
          if (!formData.policyHolderAge)
            newErrors.policyHolderAge = "Policy holder age is required";
          if (!formData.policyHolderGender)
            newErrors.policyHolderGender = "Policy holder gender is required";
          if (!formData.relationshipWithPatient)
            newErrors.relationshipWithPatient = "Relationship is required";
        }
        // New mandatory fields always required (self or not)
        if (!formData.policyHolderDob) {
          newErrors.policyHolderDob = "Policy holder DOB is required";
        } else {
          const dobDate = new Date(formData.policyHolderDob);
          const today = new Date();
          // Zero out time for consistent comparison
          dobDate.setHours(0,0,0,0);
          today.setHours(0,0,0,0);
          if (dobDate > today) {
            newErrors.policyHolderDob = "DOB cannot be in the future";
          }
        }
        if (!formData.policyHolderAlternatePhone || !formData.policyHolderAlternatePhone.trim()) {
          newErrors.policyHolderAlternatePhone = "Alternate mobile number is required";
        } else if (!/^[6-9]\d{9}$/.test(formData.policyHolderAlternatePhone.replace(/\s+/g, ""))) {
          newErrors.policyHolderAlternatePhone = "Enter a valid 10-digit mobile starting 6-9";
        }
        // Employment Information Validation (moved here from Step 5 for parity)
        if (!formData.employmentStatus)
          newErrors.employmentStatus = "Employment status is required";
        if (!formData.monthlyIncome)
          newErrors.monthlyIncome = "Monthly income is required";
        else if (parseFloat(formData.monthlyIncome) <= 0)
          newErrors.monthlyIncome = "Invalid income amount";

        // Unified validation regardless of policy type
        if (formData.employmentStatus === "employed") {
          if (!formData.workingCompanyName?.trim())
            newErrors.workingCompanyName = "Working company name is required";
          if (!formData.designation?.trim())
            newErrors.designation = "Designation is required";
        }
        if (formData.employmentStatus === "self-employed") {
          if (!formData.natureOfWork?.trim())
            newErrors.natureOfWork = "Nature of work is required";
        }
        break;

      case 3: // KYC Details (only text validation here)
        if (!formData.patientAadhar.trim()) newErrors.patientAadhar = "Aadhar number is required";
        else if (!/^\d{12}$/.test(formData.patientAadhar.replace(/\s+/g, ""))) newErrors.patientAadhar = "Invalid Aadhar number";
        // PAN - required when policy holder is self; optional otherwise
        if (formData.isSelf) {
          if (!formData.patientPan || !formData.patientPan.trim()) {
            newErrors.patientPan = "PAN is required when patient is the policy holder";
          } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.patientPan.toUpperCase())) {
            newErrors.patientPan = "Invalid PAN format";
          }
        } else if (formData.patientPan && formData.patientPan.trim()) {
          if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.patientPan.toUpperCase())) newErrors.patientPan = "Invalid PAN format";
        }
        break;

      case 4: // Medical History - optional
        break;

  case 5: // Policy Information
        if (!formData.policyType)
          newErrors.policyType = "Policy type is required";
        if (!formData.policyNumber.trim())
          newErrors.policyNumber = "Policy number is required";
        if (!formData.policyStartDate)
          newErrors.policyStartDate = "Policy start date is required";
        if (!formData.policyEndDate)
          newErrors.policyEndDate = "Policy end date is required";

        // Validate policy duration
        if (formData.policyStartDate && formData.policyEndDate) {
          const durationValidation = validatePolicyDuration(formData.policyStartDate, formData.policyEndDate);
          if (!durationValidation.valid) {
            newErrors.policyEndDate = durationValidation.message;
          }
        }

        if (!formData.insuranceCompany)
          newErrors.insuranceCompany = "Insurance company is required";
        // Corporate specific
        if (formData.policyType === "corporate") {
          if (!formData.corporateName || !formData.corporateName.trim()) {
            newErrors.corporateName = "Corporate name is required for corporate policy";
          }
        }
        break;

  case 6: // Diagnosis Details
        if (treatments.length === 0)
          newErrors.treatments = "At least one diagnosis/treatment is required";
        if (!formData.treatmentType)
          newErrors.treatmentType = "Treatment type is required";
        if (!formData.doctorName.trim())
          newErrors.doctorName = "Doctor name is required";
        if (!formData.doctorDesignation.trim())
          newErrors.doctorDesignation = "Doctor designation is required";
        if (!formData.admissionDate)
          newErrors.admissionDate = "Admission date is required";
        else {
          // Check if admission date is within 3 days (unless super admin override)
          if (!isSuperAdmin || !showAdmissionDateOverride) {
            if (!isAdmissionDateValid(formData.admissionDate)) {
              newErrors.admissionDate = "Admission date should not be older than 3 days";
            }
          }

          // Check if admission date is within policy period
          if (formData.policyStartDate && formData.policyEndDate) {
            if (!isAdmissionWithinPolicyPeriod(formData.admissionDate, formData.policyStartDate, formData.policyEndDate)) {
              if (!isSuperAdmin || !showAdmissionDateOverride) {
                newErrors.admissionDate = "Admission date should be within policy period";
              }
            }
          }
        }
        if (!formData.estimatedCost)
          newErrors.estimatedCost = "Estimated cost is required";
        else if (parseFloat(formData.estimatedCost) <= 0)
          newErrors.estimatedCost = "Invalid cost amount";
        break;
      case 7: // Upload Documents validations
        // Patient Aadhar front/back required
        if (!documents.patientAadharFrontDoc) newErrors.patientAadharFrontDoc = "Aadhar front is required";
        if (!documents.patientAadharBackDoc) newErrors.patientAadharBackDoc = "Aadhar back is required";
        // PAN and cheque/statement
        if (formData.isSelf) {
          if (!documents.patientPanDoc) newErrors.patientPanDoc = "Patient PAN is required";
          if (!documents.patientChequeOrStatementDoc) newErrors.patientChequeOrStatementDoc = "Cancelled cheque or bank statement is required";
          if (!documents.patientPolicyCopyDocs || documents.patientPolicyCopyDocs.length === 0) newErrors.patientPolicyCopyDocs = "At least one policy copy is required";
        } else {
          if (!documents.policyHolderAadharFrontDoc) newErrors.policyHolderAadharFrontDoc = "Policyholder Aadhar front is required";
          if (!documents.policyHolderAadharBackDoc) newErrors.policyHolderAadharBackDoc = "Policyholder Aadhar back is required";
          if (!documents.policyHolderPanDoc) newErrors.policyHolderPanDoc = "Policyholder PAN is required";
          if (!documents.policyHolderChequeOrStatementDoc) newErrors.policyHolderChequeOrStatementDoc = "Policyholder cheque or statement is required";
          if (!documents.policyHolderPolicyCopyDocs || documents.policyHolderPolicyCopyDocs.length === 0) newErrors.policyHolderPolicyCopyDocs = "At least one policyholder policy copy is required";
        }
        // Medical docs
        if (!documents.medicalDocs || documents.medicalDocs.length === 0) newErrors.medicalDocs = "At least one medical document is required";
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) setCurrentStep((prev) => Math.min(prev + 1, 8));
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(7)) {
      setCurrentStep(7);
      return;
    }

    if (!selectedLeadGenerator) {
      setErrors({ submit: "Lead generator not found. Please contact support." });
      return;
    }

    setLoading(true);
    try {
      // Prepare documents for upload
      const documentsToUpload = {};
      Object.entries(documents).forEach(([key, file]) => {
        if (
          file &&
          key !== "treatmentDocs" &&
          key !== "medicalHistoryDocs" &&
          key !== "medicalDocs" &&
          key !== "investigationReports" &&
          key !== "patientPolicyCopyDocs" &&
          key !== "policyHolderPolicyCopyDocs"
        ) {
          documentsToUpload[key] = file;
        }
      });

      // Add treatment documents
      Object.entries(documents.treatmentDocs || {}).forEach(([treatmentId, file]) => {
        if (file) documentsToUpload[`treatmentDoc_${treatmentId}`] = file;
      });

      // Add multi-file docs
      documents.medicalHistoryDocs.forEach((file, index) => {
        documentsToUpload[`medicalHistoryDoc_${index}`] = file;
      });
      documents.medicalDocs.forEach((file, index) => {
        documentsToUpload[`medicalDoc_${index}`] = file;
      });
      (documents.investigationReports || []).forEach((file, index) => {
        documentsToUpload[`investigationReport_${index}`] = file;
      });
      (documents.patientPolicyCopyDocs || []).forEach((file, index) => {
        documentsToUpload[`patientPolicyCopyDoc_${index}`] = file;
      });
      (documents.policyHolderPolicyCopyDocs || []).forEach((file, index) => {
        documentsToUpload[`policyHolderPolicyCopyDoc_${index}`] = file;
      });

      // Prepare final form data with lead generator info
      const finalFormData = {
        ...formData,
        treatments: treatments,
        estimatedCost: parseFloat(formData.estimatedCost) || 0,
        medicalHistoryDocsCount: documents.medicalHistoryDocs.length,
        medicalDocsCount: documents.medicalDocs.length,
        investigationReportsCount: (documents.investigationReports || []).length,
        patientPolicyCopyDocsCount: (documents.patientPolicyCopyDocs || []).length,
        policyHolderPolicyCopyDocsCount: (documents.policyHolderPolicyCopyDocs || []).length,

        // Employment Information - Explicitly map to ensure they're included
        employmentStatus: formData.employmentStatus || "",
        workingCompanyName: formData.workingCompanyName || "",
        monthlyIncome: parseFloat(formData.monthlyIncome) || 0,
        designation: formData.designation || "",
        natureOfWork: formData.natureOfWork || "",

        // Lead Generator Information
        leadGenerator: {
          id: selectedLeadGenerator.id || "",
          entityName: selectedLeadGenerator.entityName || "",
          city: selectedLeadGenerator.city || "",
          state: selectedLeadGenerator.state || "",
          servicesRequired: selectedLeadGenerator.servicesRequired || [],
          cinNumber: selectedLeadGenerator.cinNumber || "",
          leadGeneratorId: selectedLeadGenerator.leadGeneratorId || selectedLeadGenerator.id || "",
          assignedAt: new Date().toISOString(),
        },
        // Ensure medical history fields are not undefined
        medicalHistory: formData.medicalHistory || "",
        medicalDiagnosis: formData.medicalDiagnosis || "",
        medicalDuration: formData.medicalDuration || "",
        surgicalHistory: formData.surgicalHistory || "",
        surgicalDiagnosis: formData.surgicalDiagnosis || "",
        surgicalDuration: formData.surgicalDuration || "",
        surgicalProcedure: formData.surgicalProcedure || "",
      };

      // Clean up undefined values to prevent Firestore errors
      const cleanedFormData = Object.fromEntries(
        Object.entries(finalFormData).map(([key, value]) => [
          key,
          value === undefined ? "" : value
        ])
      );

      const result = await createPatientApplication(
        cleanedFormData,
        documentsToUpload,
        hospitalId,
        searchParams.get("hospitalName") || ""
      );

      if (result.success) {
        setApplicationId(result.applicationId);
        setCaseId(result.caseId || "");
        setSubmitSuccess(true);
        localStorage.removeItem('newPatientApplication');
      } else {
        setErrors({ submit: result.error });
      }
    } catch (error) {
      console.error("Application creation error:", error);
      setErrors({ submit: "Failed to create application. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const renderKycField = (fieldName, label, required = true, textRequired = true, docRequired = true) => {
    return (
      <div className="space-y-3">
        <label className="form-label">
          {label} {required && <span className="text-red-500">*</span>}
        </label>

        {/* Show requirement info */}
        {textRequired && docRequired && (
          <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
            <strong>Both details and document upload are required</strong>
          </p>
        )}
        {!textRequired && docRequired && (
          <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded border border-blue-200">
            <strong>Document upload is required.</strong> Details are optional.
          </p>
        )}

        {/* Text Input Section */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Enter Details {textRequired && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            name={fieldName}
            value={formData[fieldName]}
            onChange={handleInputChange}
            className={`input-field ${errors[fieldName] ? "input-error" : ""}`}
            placeholder={`Enter ${label.toLowerCase()}`}
          />
          {errors[fieldName] && <p className="form-error">{errors[fieldName]}</p>}
        </div>

        {/* File Upload Section */}
        {docRequired && (
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Upload Document {docRequired && <span className="text-red-500">*</span>}
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              {!documents[`${fieldName}Doc`] ? (
                <div className="text-center">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">
                    Upload {label} document
                  </p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) =>
                      handleFileUpload(`${fieldName}Doc`, e.target.files[0])
                    }
                    className="hidden"
                    id={`${fieldName}Doc`}
                  />
                  <label
                    htmlFor={`${fieldName}Doc`}
                    className="btn-primary text-sm cursor-pointer"
                  >
                    Choose File
                  </label>
                </div>
              ) : (
                <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-green-800">
                      {documents[`${fieldName}Doc`].name}
                    </span>
                  </div>
                  <button
                    onClick={() => removeFile(`${fieldName}Doc`)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            {errors[`${fieldName}Doc`] && (
              <p className="form-error">{errors[`${fieldName}Doc`]}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderStepContent = () => {
    if (submitSuccess) {
      return (
        <div className="text-center space-y-6">
          <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              Application Submitted Successfully!
            </h3>
            <p className="text-gray-600 mb-4">
              Patient application has been submitted for processing.
            </p>
            <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
              <p className="text-sm text-primary-800">
                <strong>Application ID:</strong> {applicationId}
              </p>
              {selectedLeadGenerator && (
                <p className="text-sm text-primary-800 mt-1">
                  <strong>Assigned Lead Generator:</strong> {selectedLeadGenerator.entityName}
                </p>
              )}
              <p className="text-sm text-primary-600 mt-2">
                You can track the status of this application in your dashboard.
              </p>
              {caseId && (
                <p className="text-sm text-primary-800 mt-1">
                  <strong>Case ID:</strong> {caseId}
                </p>
              )}
            </div>
          </div>

          <div className="flex space-x-4 justify-center">
            <button
              onClick={() => {
                localStorage.removeItem('newPatientApplication');
                window.location.reload();
              }}
              className="btn-primary"
            >
              Create New Application
            </button>
            <button onClick={onBack || (() => goBackToDashboard(navigate))} className="btn-outline">
              Back to Dashboard
            </button>
          </div>
        </div>
      );
    }

    switch (currentStep) {
      case 1: // Patient Information
        return (
          <div className="space-y-6">
            <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
              <h4 className="font-semibold text-primary-800 mb-2">
                Patient Information
              </h4>
              <p className="text-sm text-primary-600">
                Enter the basic details of the patient
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Patient Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="patientName"
                  value={formData.patientName}
                  onChange={handleInputChange}
                  className={`input-field ${errors.patientName ? "input-error" : ""
                    }`}
                  placeholder="Enter patient full name"
                />
                {errors.patientName && (
                  <p className="form-error">{errors.patientName}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Age <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="patientAge"
                  value={formData.patientAge}
                  onChange={handleInputChange}
                  className={`input-field ${errors.patientAge ? "input-error" : ""
                    }`}
                  placeholder="Enter age"
                  min="0"
                  max="120"
                />
                {errors.patientAge && (
                  <p className="form-error">{errors.patientAge}</p>
                )}
              </div>
            </div>

            {formData.policyType === 'corporate' && formData.corporateName && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <div className="flex items-center space-x-2">
                  <Building className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-blue-800">
                    Corporate: <strong>{formData.corporateName}</strong>
                  </span>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Gender <span className="text-red-500">*</span>
                </label>
                <select
                  name="patientGender"
                  value={formData.patientGender}
                  onChange={handleInputChange}
                  className={`input-field ${errors.patientGender ? "input-error" : ""
                    }`}
                >
                  <option value="">Select Gender</option>
                  {genderOptions.map((gender) => (
                    <option key={gender} value={gender}>
                      {gender}
                    </option>
                  ))}
                </select>
                {errors.patientGender && (
                  <p className="form-error">{errors.patientGender}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="patientPhone"
                  value={formData.patientPhone}
                  onChange={handleInputChange}
                  className={`input-field ${errors.patientPhone ? "input-error" : ""
                    }`}
                  placeholder="+91 XXXXX XXXXX"
                />
                {errors.patientPhone && (
                  <p className="form-error">{errors.patientPhone}</p>
                )}
              </div>
            </div>

            <div>
              <label className="form-label">Email Address</label>
              <input
                type="email"
                name="patientEmail"
                value={formData.patientEmail}
                onChange={handleInputChange}
                className={`input-field ${errors.patientEmail ? "input-error" : ""
                  }`}
                placeholder="patient@example.com"
              />
              {errors.patientEmail && (
                <p className="form-error">{errors.patientEmail}</p>
              )}
            </div>

            <div>
              <label className="form-label">
                Address <span className="text-red-500">*</span>
              </label>
              <textarea
                name="patientAddress"
                value={formData.patientAddress}
                onChange={handleInputChange}
                className={`input-field ${errors.patientAddress ? "input-error" : ""
                  }`}
                rows="3"
                placeholder="Enter complete address"
              />
              {errors.patientAddress && (
                <p className="form-error">{errors.patientAddress}</p>
              )}
            </div>
          </div>
        );

  case 2: // Policy Holder Information
        return (
          <div className="space-y-6">
            <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
              <h4 className="font-semibold text-primary-800 mb-2">
                Policy Holder Information
              </h4>
              <p className="text-sm text-primary-600">
                Specify who holds the insurance policy
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isSelf"
                name="isSelf"
                checked={formData.isSelf}
                onChange={handleInputChange}
                className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
              />
              <label
                htmlFor="isSelf"
                className="text-sm font-medium text-gray-700"
              >
                Patient is the policy holder
              </label>
            </div>

            {!formData.isSelf && (
              <>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">
                      Policy Holder Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="policyHolderName"
                      value={formData.policyHolderName}
                      onChange={handleInputChange}
                      className={`input-field ${errors.policyHolderName ? "input-error" : ""
                        }`}
                      placeholder="Enter policy holder name"
                    />
                    {errors.policyHolderName && (
                      <p className="form-error">{errors.policyHolderName}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Age <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="policyHolderAge"
                      value={formData.policyHolderAge}
                      onChange={handleInputChange}
                      className={`input-field ${errors.policyHolderAge ? "input-error" : ""
                        }`}
                      placeholder="Enter age"
                      min="18"
                      max="100"
                    />
                    {errors.policyHolderAge && (
                      <p className="form-error">{errors.policyHolderAge}</p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="policyHolderGender"
                      value={formData.policyHolderGender}
                      onChange={handleInputChange}
                      className={`input-field ${errors.policyHolderGender ? "input-error" : ""
                        }`}
                    >
                      <option value="">Select Gender</option>
                      {genderOptions.map((gender) => (
                        <option key={gender} value={gender}>
                          {gender}
                        </option>
                      ))}
                    </select>
                    {errors.policyHolderGender && (
                      <p className="form-error">{errors.policyHolderGender}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Relationship with Patient{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="relationshipWithPatient"
                      value={formData.relationshipWithPatient}
                      onChange={handleInputChange}
                      className={`input-field ${errors.relationshipWithPatient ? "input-error" : ""
                        }`}
                    >
                      <option value="">Select Relationship</option>
                      {relationshipOptions
                        .filter((rel) => rel !== "Self")
                        .map((relationship) => (
                          <option key={relationship} value={relationship}>
                            {relationship}
                          </option>
                        ))}
                    </select>
                    {errors.relationshipWithPatient && (
                      <p className="form-error">
                        {errors.relationshipWithPatient}
                      </p>
                    )}
                  </div>
                </div>

                {/* New mandatory fields: DOB and Alternate Mobile */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">
                      Policy Holder DOB <span className="text-red-500">*</span>
                    </label>
                    {(() => { const todayStr = new Date().toISOString().split('T')[0]; return (
                      <input
                        type="date"
                        name="policyHolderDob"
                        value={formData.policyHolderDob}
                        onChange={handleInputChange}
                        max={todayStr}
                        className={`input-field ${errors.policyHolderDob ? "input-error" : ""}`}
                      />
                    ); })()}
                    {errors.policyHolderDob && (
                      <p className="form-error">{errors.policyHolderDob}</p>
                    )}
                  </div>
                  <div>
                    <label className="form-label">
                      Alternate Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="policyHolderAlternatePhone"
                      value={formData.policyHolderAlternatePhone}
                      onChange={handleInputChange}
                      className={`input-field ${errors.policyHolderAlternatePhone ? "input-error" : ""}`}
                      placeholder="10-digit mobile number"
                    />
                    {errors.policyHolderAlternatePhone && (
                      <p className="form-error">{errors.policyHolderAlternatePhone}</p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Phone Number</label>
                    <input
                      type="tel"
                      name="policyHolderPhone"
                      value={formData.policyHolderPhone}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>

                  <div>
                    <label className="form-label">Email Address</label>
                    <input
                      type="email"
                      name="policyHolderEmail"
                      value={formData.policyHolderEmail}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="policyholder@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label">Address</label>
                  <textarea
                    name="policyHolderAddress"
                    value={formData.policyHolderAddress}
                    onChange={handleInputChange}
                    className="input-field"
                    rows="3"
                    placeholder="Enter complete address"
                  />
                </div>

                {/* Policy Holder KYC */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h5 className="font-medium text-gray-800 mb-4">
                    Policy Holder KYC Details
                  </h5>
                  <div className="space-y-4">
                    {renderKycField(
                      "policyHolderAadhar",
                      "Aadhar Number",
                      false,
                      false,
                      false
                    )}
                    {renderKycField("policyHolderPan", "PAN Number", false, false, false)}
                    {renderKycField(
                      "policyHolderAddressProof",
                      "Address Proof",
                      false,
                      false,
                      false
                    )}
                  </div>
                </div>
              </>
            )}

            {formData.isSelf && (
              <>
                {/* When patient is policy holder, show only these two fields */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">
                      Policy Holder DOB <span className="text-red-500">*</span>
                    </label>
                    {(() => { const todayStr = new Date().toISOString().split('T')[0]; return (
                      <input
                        type="date"
                        name="policyHolderDob"
                        value={formData.policyHolderDob}
                        onChange={handleInputChange}
                        max={todayStr}
                        className={`input-field ${errors.policyHolderDob ? "input-error" : ""}`}
                      />
                    ); })()}
                    {errors.policyHolderDob && (
                      <p className="form-error">{errors.policyHolderDob}</p>
                    )}
                  </div>
                  <div>
                    <label className="form-label">
                      Alternate Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="policyHolderAlternatePhone"
                      value={formData.policyHolderAlternatePhone}
                      onChange={handleInputChange}
                      className={`input-field ${errors.policyHolderAlternatePhone ? "input-error" : ""}`}
                      placeholder="10-digit mobile number"
                    />
                    {errors.policyHolderAlternatePhone && (
                      <p className="form-error">{errors.policyHolderAlternatePhone}</p>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Nominee Information (Optional) */}
            <div className="bg-secondary-50 rounded-lg p-4 border border-secondary-200">
              <h5 className="font-semibold text-secondary-800 mb-2">
                Nominee Information (Optional)
              </h5>
              <p className="text-sm text-secondary-600 mb-4">
                Person who will receive benefits in case of unfortunate events. These details are optional and can be added later.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">
                    Nominee Name
                  </label>
                  <input
                    type="text"
                    name="nomineeName"
                    value={formData.nomineeName}
                    onChange={handleInputChange}
                    className={`input-field ${errors.nomineeName ? "input-error" : ""}`}
                    placeholder="Enter nominee name"
                  />
                  {errors.nomineeName && (
                    <p className="form-error">{errors.nomineeName}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">
                    Relationship with Nominee
                  </label>
                  <select
                    name="relationshipWithNominee"
                    value={formData.relationshipWithNominee}
                    onChange={handleInputChange}
                    className={`input-field ${errors.relationshipWithNominee ? "input-error" : ""}`}
                  >
                    <option value="">Select Relationship</option>
                    {relationshipOptions.map((relationship) => (
                      <option key={relationship} value={relationship}>
                        {relationship}
                      </option>
                    ))}
                  </select>
                  {errors.relationshipWithNominee && (
                    <p className="form-error">
                      {errors.relationshipWithNominee}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="form-label">
                  Nominee Contact
                </label>
                <input
                  type="tel"
                  name="nomineeContact"
                  value={formData.nomineeContact}
                  onChange={handleInputChange}
                  className={`input-field ${errors.nomineeContact ? "input-error" : ""}`}
                  placeholder="+91 XXXXX XXXXX"
                />
                {errors.nomineeContact && (
                  <p className="form-error">{errors.nomineeContact}</p>
                )}
              </div>
            </div>

            {/* Employment Information Section (moved here for parity) */}
            <div className="bg-secondary-50 rounded-lg p-4 border border-secondary-200">
              <h4 className="font-semibold text-secondary-800 mb-4">
                Employment Information
              </h4>

              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">
                      Employment Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="employmentStatus"
                      value={formData.employmentStatus}
                      onChange={handleInputChange}
                      className={`input-field ${errors.employmentStatus ? "input-error" : ""}`}
                    >
                      <option value="">Select Employment Status</option>
                      <option value="employed">Employed</option>
                      <option value="self-employed">Self Employed</option>
                      <option value="unemployed">Unemployed</option>
                      <option value="retired">Retired</option>
                      <option value="student">Student</option>
                      <option value="homemaker">Homemaker</option>
                    </select>
                    {errors.employmentStatus && (
                      <p className="form-error">{errors.employmentStatus}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Monthly Income (₹) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="monthlyIncome"
                      value={formData.monthlyIncome}
                      onChange={handleInputChange}
                      className={`input-field ${errors.monthlyIncome ? "input-error" : ""}`}
                      placeholder="Enter monthly income"
                      min="0"
                    />
                    {errors.monthlyIncome && (
                      <p className="form-error">{errors.monthlyIncome}</p>
                    )}
                  </div>
                </div>

                {/* Conditional fields */}
                {formData.employmentStatus === "employed" && (
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">
                        Working Company Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="workingCompanyName"
                        value={formData.workingCompanyName}
                        onChange={handleInputChange}
                        className={`input-field ${errors.workingCompanyName ? "input-error" : ""}`}
                        placeholder="Enter company name"
                      />
                      {errors.workingCompanyName && (
                        <p className="form-error">{errors.workingCompanyName}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label">
                        Designation <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                        className={`input-field ${errors.designation ? "input-error" : ""}`}
                        placeholder="e.g., Manager, Executive, Officer"
                      />
                      {errors.designation && (
                        <p className="form-error">{errors.designation}</p>
                      )}
                    </div>
                  </div>
                )}

                {formData.employmentStatus === "self-employed" && (
                  <div className="grid md:grid-cols-1 gap-6">
                    <div>
                      <label className="form-label">
                        Nature of Work <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="natureOfWork"
                        value={formData.natureOfWork}
                        onChange={handleInputChange}
                        className={`input-field ${errors.natureOfWork ? "input-error" : ""}`}
                        placeholder="e.g., Business, Freelancing, Consulting"
                      />
                      {errors.natureOfWork && (
                        <p className="form-error">{errors.natureOfWork}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 3: // KYC Details (numbers only; documents in step 7)
        return (
          <div className="space-y-6">
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800">
                    Patient KYC Details
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Provide KYC numbers only. You'll upload documents in Step 7.
                  </p>
                </div>
              </div>
            </div>

            {renderKycField("patientAadhar", "Aadhar Number", true, true, false)}
            {renderKycField("patientPan", "PAN Number", false, true, false)}
            {renderKycField("patientAddressProof", "Address Proof", false, false, false)}
          </div>
        );

      case 4: // Medical History
        return (
          <div className="space-y-6">
            <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
              <h4 className="font-semibold text-primary-800 mb-2">
                Patient Medical History
              </h4>
              <p className="text-sm text-primary-600">
                Provide details about the patient's medical and surgical history
              </p>
            </div>

            {/* Checkbox for Medical History */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="hasMedicalHistory"
                name="hasMedicalHistory"
                checked={formData.hasMedicalHistory}
                onChange={handleInputChange}
                className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
              />
              <label
                htmlFor="hasMedicalHistory"
                className="text-sm font-medium text-gray-700"
              >
                Patient has previous medical history
              </label>
            </div>

            {formData.hasMedicalHistory ? (
              <>
                {/* Medical History Section */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h5 className="font-semibold text-blue-800 mb-4">Medical History</h5>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Medical History Description</label>
                      <textarea
                        name="medicalHistory"
                        value={formData.medicalHistory}
                        onChange={handleInputChange}
                        className="input-field"
                        rows="3"
                        placeholder="Previous medical conditions, chronic diseases, medications, etc."
                      />
                    </div>
                    <div>
                      <label className="form-label">Diagnosis Information</label>
                      <textarea
                        name="medicalDiagnosis"
                        value={formData.medicalDiagnosis}
                        onChange={handleInputChange}
                        className="input-field"
                        rows="3"
                        placeholder="Specific diagnosis details, medical conditions, etc."
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="form-label">Duration</label>
                    <input
                      type="text"
                      name="medicalDuration"
                      value={formData.medicalDuration}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="e.g., 2 years, 6 months, ongoing"
                    />
                  </div>
                </div>

                {/* Surgical History Section */}
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <h5 className="font-semibold text-purple-800 mb-4">Surgical History</h5>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Surgical History Description</label>
                      <textarea
                        name="surgicalHistory"
                        value={formData.surgicalHistory}
                        onChange={handleInputChange}
                        className="input-field"
                        rows="3"
                        placeholder="Previous surgeries, operations, procedures, etc."
                      />
                    </div>
                    <div>
                      <label className="form-label">Surgical Diagnosis Information</label>
                      <textarea
                        name="surgicalDiagnosis"
                        value={formData.surgicalDiagnosis}
                        onChange={handleInputChange}
                        className="input-field"
                        rows="3"
                        placeholder="Diagnosis that led to surgery, surgical indications, etc."
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <label className="form-label">Duration</label>
                      <input
                        type="text"
                        name="surgicalDuration"
                        value={formData.surgicalDuration}
                        onChange={handleInputChange}
                        className="input-field"
                        placeholder="e.g., 1 year ago, 3 months ago"
                      />
                    </div>
                    <div>
                      <label className="form-label">Procedure Information</label>
                      <input
                        type="text"
                        name="surgicalProcedure"
                        value={formData.surgicalProcedure}
                        onChange={handleInputChange}
                        className="input-field"
                        placeholder="Name of surgical procedure performed"
                      />
                    </div>
                  </div>
                </div>

                {/* Medical History Documents */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h5 className="font-medium text-gray-800 mb-4">
                    Medical History Documents
                  </h5>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload previous medical reports, test results, discharge summaries, etc. (Optional)
                  </p>

                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <div className="text-center">
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 mb-2">
                        Upload medical history documents
                      </p>
                      <p className="text-xs text-gray-500 mb-3">
                        PDF, JPEG, PNG up to 100MB each. Multiple files allowed.
                      </p>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        multiple
                        onChange={(e) => handleMedicalHistoryDocUpload(e.target.files)}
                        className="hidden"
                        id="medicalHistoryDocs"
                      />
                      <label
                        htmlFor="medicalHistoryDocs"
                        className="btn-primary cursor-pointer"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Choose Files
                      </label>
                    </div>
                  </div>

                  {errors.medicalHistoryDocs && (
                    <p className="form-error mt-2">{errors.medicalHistoryDocs}</p>
                  )}

                  {/* Display uploaded medical history documents */}
                  {documents.medicalHistoryDocs.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <h6 className="font-medium text-gray-700">Uploaded Documents:</h6>
                      {documents.medicalHistoryDocs.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                          <div className="flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-green-800">{file.name}</span>
                            <span className="text-xs text-green-600">
                              ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                          <button
                            onClick={() => removeMedicalHistoryDoc(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-800 font-medium">
                      No Medical History Declared
                    </p>
                    <p className="text-sm text-red-700 mt-1">
                      <strong>Warning:</strong> You have declared that the patient has no previous medical history.
                      If this information is found to be incorrect or misleading during the claim process,
                      the application may be rejected and you will be held responsible for providing false information.
                    </p>
                    <p className="text-sm text-red-600 mt-2 font-medium">
                      Please ensure this declaration is accurate and complete.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

  case 5: // Policy Information (moved before diagnosis)
        return (
          <div className="space-y-6">
            <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
              <h4 className="font-semibold text-primary-800 mb-2">
                Insurance Policy Information
              </h4>
              <p className="text-sm text-primary-600">
                Enter the details of the insurance policy
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Policy Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="policyType"
                  value={formData.policyType}
                  onChange={handleInputChange}
                  className={`input-field ${errors.policyType ? "input-error" : ""
                    }`}
                >
                  <option value="">Select Policy Type</option>
                  <option value="retail">Retail</option>
                  <option value="corporate">Corporate</option>
                </select>
                {errors.policyType && (
                  <p className="form-error">{errors.policyType}</p>
                )}
              </div>

              {formData.policyType === "corporate" && (
                <div>
                  <label className="form-label">
                    Corporate Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="corporateName"
                    value={formData.corporateName}
                    onChange={handleInputChange}
                    className={`input-field ${errors.corporateName ? "input-error" : ""}`}
                    placeholder="Enter corporate/employer name"
                  />
                  {errors.corporateName && (
                    <p className="form-error">{errors.corporateName}</p>
                  )}
                </div>
              )}

              <div>
                <label className="form-label">
                  Policy Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="policyNumber"
                  value={formData.policyNumber}
                  onChange={handleInputChange}
                  className={`input-field ${errors.policyNumber ? "input-error" : ""
                    }`}
                  placeholder="Enter policy number"
                />
                {errors.policyNumber && (
                  <p className="form-error">{errors.policyNumber}</p>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Policy Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="policyStartDate"
                  value={formData.policyStartDate}
                  onChange={handleInputChange}
                  className={`input-field ${errors.policyStartDate ? "input-error" : ""
                    }`}
                />
                {errors.policyStartDate && (
                  <p className="form-error">{errors.policyStartDate}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Policy End Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="policyEndDate"
                  value={formData.policyEndDate}
                  onChange={handleInputChange}
                  className={`input-field ${errors.policyEndDate ? "input-error" : ""
                    }`}
                />
                {errors.policyEndDate && (
                  <p className="form-error">{errors.policyEndDate}</p>
                )}
              </div>
            </div>

            {/* Policy Duration and Expiry Validation Messages */}
            {formData.policyStartDate && formData.policyEndDate && (
              <div className="space-y-3">
                {(() => {
                  const durationValidation = validatePolicyDuration(formData.policyStartDate, formData.policyEndDate);
                  const expiryStatus = checkPolicyExpiry(formData.policyEndDate);

                  return (
                    <>
                      {/* Duration Validation */}
                      {durationValidation.valid ? (
                        <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-green-800 font-medium">
                              {durationValidation.message}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                          <div className="flex items-center space-x-2">
                            <AlertCircle className="h-4 w-4 text-red-600" />
                            <span className="text-sm text-red-800 font-medium">
                              {durationValidation.message}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Expiry Status */}
                      {expiryStatus.status === 'expired' && (
                        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                          <div className="flex items-start space-x-3">
                            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                            <div>
                              <p className="text-sm text-red-800 font-medium">Policy Expired</p>
                              <p className="text-sm text-red-700 mt-1">{expiryStatus.message}</p>
                              <div className="mt-3 flex items-center space-x-3">
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    name="policyRenewalStatus"
                                    value="renewed"
                                    className="mr-2"
                                  />
                                  <span className="text-sm">Policy has been renewed</span>
                                </label>
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    name="policyRenewalStatus"
                                    value="inprocess"
                                    className="mr-2"
                                  />
                                  <span className="text-sm">Renewal in process</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {expiryStatus.status === 'expiring' && (
                        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                          <div className="flex items-start space-x-3">
                            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                            <div>
                              <p className="text-sm text-amber-800 font-medium">Policy Expiring Soon</p>
                              <p className="text-sm text-amber-700 mt-1">{expiryStatus.message}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Insurance Company <span className="text-red-500">*</span>
                </label>
                <select
                  name="insuranceCompany"
                  value={formData.insuranceCompany}
                  onChange={handleInputChange}
                  className={`input-field ${errors.insuranceCompany ? "input-error" : ""
                    }`}
                >
                  <option value="">Select Insurance Company</option>
                  {insuranceCompanies.map((company) => (
                    <option key={company} value={company}>
                      {company}
                    </option>
                  ))}
                </select>
                {errors.insuranceCompany && (
                  <p className="form-error">{errors.insuranceCompany}</p>
                )}
              </div>

              <div>
                <label className="form-label">TPA Name</label>
                <select
                  name="tpaName"
                  value={formData.tpaName}
                  onChange={handleInputChange}
                  className="input-field"
                >
                  <option value="">Select TPA Company</option>
                  {tpaCompanies.map((tpa) => (
                    <option key={tpa} value={tpa}>
                      {tpa}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Policy Document upload moved to Step 7 (Upload Documents) */}
          </div>
        );

      case 6: // Diagnosis Details
        return (
          <div className="space-y-6">
            <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
              <h4 className="font-semibold text-primary-800 mb-2">
                Medical Diagnosis & Treatment Details
              </h4>
              <p className="text-sm text-primary-600">
                Add multiple diagnoses and treatments for the patient
              </p>
            </div>

            {/* Add New Treatment Section */}
            <div className="bg-secondary-50 rounded-lg p-6 border border-secondary-200">
              <h5 className="font-semibold text-secondary-800 mb-4">
                Add Diagnosis & Treatment
              </h5>

              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">
                      Diagnosis Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newTreatment.diagnosisName}
                      onChange={(e) =>
                        setNewTreatment((prev) => ({
                          ...prev,
                          diagnosisName: e.target.value,
                        }))
                      }
                      className={`input-field ${errors.diagnosisName ? "input-error" : ""
                        }`}
                      placeholder="Enter diagnosis name"
                    />
                    {errors.diagnosisName && (
                      <p className="form-error">{errors.diagnosisName}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">Treatment Type</label>
                    <select
                      value={newTreatment.treatmentType}
                      onChange={(e) =>
                        setNewTreatment((prev) => ({
                          ...prev,
                          treatmentType: e.target.value,
                        }))
                      }
                      className="input-field"
                    >
                      <option value="">Select Treatment Type</option>
                      {treatmentTypes.map((type) => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Procedure Name</label>
                    <input
                      type="text"
                      value={newTreatment.procedureName}
                      onChange={(e) =>
                        setNewTreatment((prev) => ({
                          ...prev,
                          procedureName: e.target.value,
                        }))
                      }
                      className="input-field"
                      placeholder="Enter procedure name"
                    />
                  </div>

                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={addTreatment}
                      className="btn-primary w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Treatment
                    </button>
                  </div>
                </div>

                <div>
                  <label className="form-label">Description</label>
                  <textarea
                    value={newTreatment.description}
                    onChange={(e) =>
                      setNewTreatment((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    className="input-field"
                    rows="2"
                    placeholder="Additional details about the treatment"
                  />
                </div>
              </div>
            </div>

            {/* Display Added Treatments */}
            {treatments.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h5 className="font-medium text-gray-800 mb-4">
                  Added Diagnoses & Treatments
                </h5>
                <div className="space-y-3">
                  {treatments.map((treatment) => (
                    <div
                      key={treatment.id}
                      className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="grid md:grid-cols-3 gap-4">
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Diagnosis
                              </span>
                              <p className="text-sm font-semibold text-gray-900 mt-1">
                                {treatment.diagnosisName}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Treatment
                              </span>
                              <p className="text-sm text-gray-700 mt-1">
                                {treatment.treatmentType || "Not specified"}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Procedure
                              </span>
                              <p className="text-sm text-gray-700 mt-1">
                                {treatment.procedureName || "Not specified"}
                              </p>
                            </div>
                          </div>
                          {treatment.description && (
                            <div className="mt-3">
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Description
                              </span>
                              <p className="text-sm text-gray-700 mt-1">
                                {treatment.description}
                              </p>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => removeTreatment(treatment.id)}
                          className="ml-4 text-red-500 hover:text-red-700 flex-shrink-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Treatment Document Upload */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Treatment Document
                        </label>
                        {!documents.treatmentDocs?.[treatment.id] ? (
                          <div className="mt-2">
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) =>
                                handleTreatmentDocUpload(
                                  treatment.id,
                                  e.target.files[0]
                                )
                              }
                              className="hidden"
                              id={`treatmentDoc_${treatment.id}`}
                            />
                            <label
                              htmlFor={`treatmentDoc_${treatment.id}`}
                              className="cursor-pointer inline-flex items-center text-xs text-secondary-600 hover:text-secondary-800"
                            >
                              <Upload className="h-3 w-3 mr-1" />
                              Upload Document
                            </label>
                          </div>
                        ) : (
                          <div className="mt-2 flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-green-600" />
                            <span className="text-xs text-green-800">
                              {documents.treatmentDocs[treatment.id].name}
                            </span>
                            <button
                              onClick={() => {
                                setDocuments((prev) => {
                                  const newDocs = { ...prev };
                                  delete newDocs.treatmentDocs[treatment.id];
                                  return newDocs;
                                });
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                        {errors[`treatmentDoc_${treatment.id}`] && (
                          <p className="text-xs text-red-600 mt-1">
                            {errors[`treatmentDoc_${treatment.id}`]}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Treatment validation error */}
            {errors.treatments && (
              <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                <p className="text-sm text-red-800">{errors.treatments}</p>
              </div>
            )}

            {/* Additional Information */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h5 className="font-medium text-gray-800 mb-4">
                Treatment Information
              </h5>

              <div className="grid md:grid-cols-2 gap-6 mb-4">
                <div>
                  <label className="form-label">Treatment Plan</label>
                  <select
                    name="treatmentPlanned"
                    value={formData.treatmentPlanned}
                    onChange={handleInputChange}
                    className="input-field"
                  >
                    <option value="planned">Planned</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Priority Level</label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    className="input-field"
                  >
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="emergency"
                  name="emergency"
                  checked={formData.emergency}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-red-500 focus:ring-red-500"
                />
                <label
                  htmlFor="emergency"
                  className="text-sm font-medium text-gray-700"
                >
                  Emergency Case
                </label>
              </div>
            </div>

            {/* Doctor and Hospital Information */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Doctor Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="doctorName"
                  value={formData.doctorName}
                  onChange={handleInputChange}
                  className={`input-field ${errors.doctorName ? "input-error" : ""
                    }`}
                  placeholder="Dr. Full Name"
                />
                {errors.doctorName && (
                  <p className="form-error">{errors.doctorName}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Doctor Designation <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="doctorDesignation"
                  value={formData.doctorDesignation}
                  onChange={handleInputChange}
                  className={`input-field ${errors.doctorDesignation ? "input-error" : ""
                    }`}
                  placeholder="e.g., Cardiologist, Surgeon"
                />
                {errors.doctorDesignation && (
                  <p className="form-error">{errors.doctorDesignation}</p>
                )}
              </div>
            </div>

            {/* Admission and Discharge Dates */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">
                  Admission Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="admissionDate"
                  value={formData.admissionDate}
                  onChange={handleInputChange}
                  className={`input-field ${errors.admissionDate ? "input-error" : ""
                    }`}
                />
                {errors.admissionDate && (
                  <p className="form-error">{errors.admissionDate}</p>
                )}

                {/* Super Admin Override Option */}
                {isSuperAdmin && (
                  <div className="mt-2">
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={showAdmissionDateOverride}
                        onChange={(e) => setShowAdmissionDateOverride(e.target.checked)}
                        className="mr-2 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                      />
                      <span className="text-amber-700">Override date restrictions (Admin)</span>
                    </label>
                  </div>
                )}

                {formData.admissionDate && !isAdmissionDateValid(formData.admissionDate) && !showAdmissionDateOverride && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      <span>Warning: Admission date is older than 3 days</span>
                    </div>
                  </div>
                )}

                {formData.admissionDate && formData.policyStartDate && formData.policyEndDate &&
                  !isAdmissionWithinPolicyPeriod(formData.admissionDate, formData.policyStartDate, formData.policyEndDate) &&
                  !showAdmissionDateOverride && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                      <div className="flex items-center">
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        <span>Error: Admission date is outside policy period</span>
                      </div>
                    </div>
                  )}
              </div>

              <div>
                <label className="form-label">Discharge Date</label>
                <input
                  type="date"
                  name="dischargeDate"
                  value={formData.dischargeDate}
                  onChange={handleInputChange}
                  className="input-field"
                />
              </div>
            </div>

            {/* Cost Information */}
            <div>
              <label className="form-label">
                Estimated Cost (₹) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="estimatedCost"
                value={formData.estimatedCost}
                onChange={handleInputChange}
                className={`input-field ${errors.estimatedCost ? "input-error" : ""
                  }`}
                placeholder="Enter estimated treatment cost"
                min="0"
              />
              {errors.estimatedCost && (
                <p className="form-error">{errors.estimatedCost}</p>
              )}
            </div>

            {/* Remarks */}
            <div>
              <label className="form-label">Remarks</label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                className="input-field"
                rows="3"
                placeholder="Any additional remarks or notes about the case"
              />
            </div>
          </div>
        );

      case 7: // Upload Documents
        return (
          <div className="space-y-6">
            {/* Info */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-start space-x-3">
                <Upload className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-800">Upload Required Documents</h4>
                  <p className="text-sm text-blue-700 mt-1">Upload KYC, policy copies, and medical documents.</p>
                </div>
              </div>
            </div>

            {/* Patient KYC Uploads */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h5 className="font-semibold text-gray-800 mb-3">Patient KYC</h5>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Aadhar Front */}
                <div>
                  <label className="form-label">Aadhar Front <span className="text-red-500">*</span></label>
                  {!documents.patientAadharFrontDoc ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload('patientAadharFrontDoc', e.target.files[0])} className="hidden" id="patientAadharFrontDoc" />
                      <label htmlFor="patientAadharFrontDoc" className="btn-primary cursor-pointer">Choose File</label>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-5 w-5 text-green-600" />
                        <span className="text-sm">{documents.patientAadharFrontDoc.name}</span>
                      </div>
                      <button onClick={() => removeFile('patientAadharFrontDoc')} className="text-red-500"><X className="h-4 w-4" /></button>
                    </div>
                  )}
                  {errors.patientAadharFrontDoc && <p className="form-error">{errors.patientAadharFrontDoc}</p>}
                </div>
                {/* Aadhar Back */}
                <div>
                  <label className="form-label">Aadhar Back <span className="text-red-500">*</span></label>
                  {!documents.patientAadharBackDoc ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload('patientAadharBackDoc', e.target.files[0])} className="hidden" id="patientAadharBackDoc" />
                      <label htmlFor="patientAadharBackDoc" className="btn-primary cursor-pointer">Choose File</label>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-5 w-5 text-green-600" />
                        <span className="text-sm">{documents.patientAadharBackDoc.name}</span>
                      </div>
                      <button onClick={() => removeFile('patientAadharBackDoc')} className="text-red-500"><X className="h-4 w-4" /></button>
                    </div>
                  )}
                  {errors.patientAadharBackDoc && <p className="form-error">{errors.patientAadharBackDoc}</p>}
                </div>
                {/* PAN */}
                <div>
                  <label className="form-label">PAN <span className="text-red-500">*</span></label>
                  {!documents.patientPanDoc ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload('patientPanDoc', e.target.files[0])} className="hidden" id="patientPanDoc" />
                      <label htmlFor="patientPanDoc" className="btn-primary cursor-pointer">Choose File</label>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-5 w-5 text-green-600" />
                        <span className="text-sm">{documents.patientPanDoc.name}</span>
                      </div>
                      <button onClick={() => removeFile('patientPanDoc')} className="text-red-500"><X className="h-4 w-4" /></button>
                    </div>
                  )}
                  {errors.patientPanDoc && <p className="form-error">{errors.patientPanDoc}</p>}
                </div>
                {/* Cheque/Statement */}
                <div>
                  <label className="form-label">Cancelled Cheque or Bank Statement <span className="text-red-500">*</span></label>
                  {!documents.patientChequeOrStatementDoc ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload('patientChequeOrStatementDoc', e.target.files[0])} className="hidden" id="patientChequeOrStatementDoc" />
                      <label htmlFor="patientChequeOrStatementDoc" className="btn-primary cursor-pointer">Choose File</label>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-5 w-5 text-green-600" />
                        <span className="text-sm">{documents.patientChequeOrStatementDoc.name}</span>
                      </div>
                      <button onClick={() => removeFile('patientChequeOrStatementDoc')} className="text-red-500"><X className="h-4 w-4" /></button>
                    </div>
                  )}
                  {errors.patientChequeOrStatementDoc && <p className="form-error">{errors.patientChequeOrStatementDoc}</p>}
                </div>
              </div>
            </div>

            {/* Policy Holder KYC (if applicable) */}
            {!formData.isSelf && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h5 className="font-semibold text-gray-800 mb-3">Policy Holder KYC</h5>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Aadhar Front */}
                  <div>
                    <label className="form-label">Aadhar Front <span className="text-red-500">*</span></label>
                    {!documents.policyHolderAadharFrontDoc ? (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                        <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload('policyHolderAadharFrontDoc', e.target.files[0])} className="hidden" id="policyHolderAadharFrontDoc" />
                        <label htmlFor="policyHolderAadharFrontDoc" className="btn-primary cursor-pointer">Choose File</label>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-5 w-5 text-green-600" />
                          <span className="text-sm">{documents.policyHolderAadharFrontDoc.name}</span>
                        </div>
                        <button onClick={() => removeFile('policyHolderAadharFrontDoc')} className="text-red-500"><X className="h-4 w-4" /></button>
                      </div>
                    )}
                    {errors.policyHolderAadharFrontDoc && <p className="form-error">{errors.policyHolderAadharFrontDoc}</p>}
                  </div>
                  {/* Aadhar Back */}
                  <div>
                    <label className="form-label">Aadhar Back <span className="text-red-500">*</span></label>
                    {!documents.policyHolderAadharBackDoc ? (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                        <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload('policyHolderAadharBackDoc', e.target.files[0])} className="hidden" id="policyHolderAadharBackDoc" />
                        <label htmlFor="policyHolderAadharBackDoc" className="btn-primary cursor-pointer">Choose File</label>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-5 w-5 text-green-600" />
                          <span className="text-sm">{documents.policyHolderAadharBackDoc.name}</span>
                        </div>
                        <button onClick={() => removeFile('policyHolderAadharBackDoc')} className="text-red-500"><X className="h-4 w-4" /></button>
                      </div>
                    )}
                    {errors.policyHolderAadharBackDoc && <p className="form-error">{errors.policyHolderAadharBackDoc}</p>}
                  </div>
                  {/* PAN */}
                  <div>
                    <label className="form-label">PAN <span className="text-red-500">*</span></label>
                    {!documents.policyHolderPanDoc ? (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                        <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload('policyHolderPanDoc', e.target.files[0])} className="hidden" id="policyHolderPanDoc" />
                        <label htmlFor="policyHolderPanDoc" className="btn-primary cursor-pointer">Choose File</label>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-5 w-5 text-green-600" />
                          <span className="text-sm">{documents.policyHolderPanDoc.name}</span>
                        </div>
                        <button onClick={() => removeFile('policyHolderPanDoc')} className="text-red-500"><X className="h-4 w-4" /></button>
                      </div>
                    )}
                    {errors.policyHolderPanDoc && <p className="form-error">{errors.policyHolderPanDoc}</p>}
                  </div>
                  {/* Cheque/Statement */}
                  <div>
                    <label className="form-label">Cancelled Cheque or Bank Statement <span className="text-red-500">*</span></label>
                    {!documents.policyHolderChequeOrStatementDoc ? (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                        <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload('policyHolderChequeOrStatementDoc', e.target.files[0])} className="hidden" id="policyHolderChequeOrStatementDoc" />
                        <label htmlFor="policyHolderChequeOrStatementDoc" className="btn-primary cursor-pointer">Choose File</label>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-5 w-5 text-green-600" />
                          <span className="text-sm">{documents.policyHolderChequeOrStatementDoc.name}</span>
                        </div>
                        <button onClick={() => removeFile('policyHolderChequeOrStatementDoc')} className="text-red-500"><X className="h-4 w-4" /></button>
                      </div>
                    )}
                    {errors.policyHolderChequeOrStatementDoc && <p className="form-error">{errors.policyHolderChequeOrStatementDoc}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Policy Copy Uploads */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h5 className="font-semibold text-gray-800 mb-3">Policy Copy</h5>
              <div className="space-y-2">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">Upload policy copy images or PDFs (multiple allowed)</p>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={(e) => formData.isSelf ? handlePatientPolicyCopyUpload(e.target.files) : handlePolicyHolderPolicyCopyUpload(e.target.files)} className="hidden" id="policyCopyDocs" />
                  <label htmlFor="policyCopyDocs" className="btn-primary cursor-pointer">Choose Files</label>
                </div>
                {formData.isSelf ? (
                  <>
                    {errors.patientPolicyCopyDocs && <p className="form-error">{errors.patientPolicyCopyDocs}</p>}
                    {(documents.patientPolicyCopyDocs || []).length > 0 && (
                      <div className="space-y-2">
                        {(documents.patientPolicyCopyDocs || []).map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                            <div className="flex items-center space-x-2">
                              <FileText className="h-4 w-4 text-green-600" />
                              <span className="text-sm text-green-800">{file.name}</span>
                            </div>
                            <button onClick={() => setDocuments((prev) => ({ ...prev, patientPolicyCopyDocs: prev.patientPolicyCopyDocs.filter((_, i) => i !== idx) }))} className="text-red-500"><X className="h-4 w-4" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {errors.policyHolderPolicyCopyDocs && <p className="form-error">{errors.policyHolderPolicyCopyDocs}</p>}
                    {(documents.policyHolderPolicyCopyDocs || []).length > 0 && (
                      <div className="space-y-2">
                        {(documents.policyHolderPolicyCopyDocs || []).map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                            <div className="flex items-center space-x-2">
                              <FileText className="h-4 w-4 text-green-600" />
                              <span className="text-sm text-green-800">{file.name}</span>
                            </div>
                            <button onClick={() => setDocuments((prev) => ({ ...prev, policyHolderPolicyCopyDocs: prev.policyHolderPolicyCopyDocs.filter((_, i) => i !== idx) }))} className="text-red-500"><X className="h-4 w-4" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Medical Documents */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h5 className="font-semibold text-gray-800 mb-3">Medical Documents</h5>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Medical Docs */}
                <div>
                  <label className="form-label">Medical Documents <span className="text-red-500">*</span></label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={(e) => handleMedicalDocsUpload(e.target.files)} className="hidden" id="medicalDocsUpload" />
                    <label htmlFor="medicalDocsUpload" className="btn-primary cursor-pointer">Choose Files</label>
                  </div>
                  {errors.medicalDocs && <p className="form-error">{errors.medicalDocs}</p>}
                  {(documents.medicalDocs || []).length > 0 && (
                    <div className="mt-2 space-y-2">
                      {(documents.medicalDocs || []).map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                          <div className="flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-green-800">{file.name}</span>
                          </div>
                          <button onClick={() => setDocuments((prev) => ({ ...prev, medicalDocs: prev.medicalDocs.filter((_, i) => i !== idx) }))} className="text-red-500"><X className="h-4 w-4" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Investigation Reports (optional) */}
                <div>
                  <label className="form-label">Investigation Reports (optional)</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={(e) => handleInvestigationReportsUpload(e.target.files)} className="hidden" id="investigationReportsUpload" />
                    <label htmlFor="investigationReportsUpload" className="btn-primary cursor-pointer">Choose Files</label>
                  </div>
                  {(documents.investigationReports || []).length > 0 && (
                    <div className="mt-2 space-y-2">
                      {(documents.investigationReports || []).map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                          <div className="flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-green-800">{file.name}</span>
                          </div>
                          <button onClick={() => setDocuments((prev) => ({ ...prev, investigationReports: prev.investigationReports.filter((_, i) => i !== idx) }))} className="text-red-500"><X className="h-4 w-4" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 8: // Review & Submit
        return (
          <div className="space-y-6">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-green-800">
                    Review Application
                  </h4>
                  <p className="text-sm text-green-700 mt-1">
                    Please review all information before submitting the
                    application
                  </p>
                </div>
              </div>
            </div>

            {/* Review Summary */}
            <div className="space-y-4">
              {/* Patient Information */}
              <div className="card">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <User className="h-5 w-5 mr-2 text-primary-500" />
                  Patient Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Name:</span>{" "}
                    {formData.patientName}
                  </div>
                  <div>
                    <span className="font-medium">Age:</span>{" "}
                    {formData.patientAge} years
                  </div>
                  <div>
                    <span className="font-medium">Gender:</span>{" "}
                    {formData.patientGender}
                  </div>
                  <div>
                    <span className="font-medium">Phone:</span>{" "}
                    {formData.patientPhone}
                  </div>
                  <div className="md:col-span-2">
                    <span className="font-medium">Address:</span>{" "}
                    {formData.patientAddress}
                  </div>
                </div>
              </div>

              {/* Medical History */}
              {(formData.medicalHistory || formData.surgicalHistory || documents.medicalHistoryDocs.length > 0) && (
                <div className="card">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <History className="h-5 w-5 mr-2 text-primary-500" />
                    Medical History
                  </h4>
                  <div className="space-y-2 text-sm">
                    {formData.medicalHistory && (
                      <div>
                        <span className="font-medium">Medical History:</span>{" "}
                        {formData.medicalHistory}
                      </div>
                    )}
                    {formData.surgicalHistory && (
                      <div>
                        <span className="font-medium">Surgical History:</span>{" "}
                        {formData.surgicalHistory}
                      </div>
                    )}
                    {documents.medicalHistoryDocs.length > 0 && (
                      <div>
                        <span className="font-medium">Documents:</span> {documents.medicalHistoryDocs.length} files uploaded
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Policy Information */}
              <div className="card">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <CreditCard className="h-5 w-5 mr-2 text-primary-500" />
                  Policy Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Policy Type:</span>{" "}
                    {formData.policyType}
                  </div>
                  <div>
                    <span className="font-medium">Policy Number:</span>{" "}
                    {formData.policyNumber}
                  </div>
                  <div>
                    <span className="font-medium">Insurance Company:</span>{" "}
                    {formData.insuranceCompany}
                  </div>
                  <div>
                    <span className="font-medium">Policy Period:</span>{" "}
                    {formData.policyStartDate} to {formData.policyEndDate}
                  </div>
                  {formData.tpaName && (
                    <div>
                      <span className="font-medium">TPA:</span>{" "}
                      {formData.tpaName}
                    </div>
                  )}
                </div>
              </div>

              {/* Employment Information */}
              <div className="card">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <CreditCard className="h-5 w-5 mr-2 text-primary-500" />
                  Employment Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Employment Status:</span>{" "}
                    {formData.employmentStatus.charAt(0).toUpperCase() + formData.employmentStatus.slice(1)}
                  </div>
                  <div>
                    <span className="font-medium">Monthly Income:</span> ₹
                    {parseFloat(formData.monthlyIncome || 0).toLocaleString()}
                  </div>
                  {formData.policyType === "retail" && formData.workingCompanyName && (
                    <div>
                      <span className="font-medium">Company:</span>{" "}
                      {formData.workingCompanyName}
                    </div>
                  )}
                  {formData.employmentStatus === "self-employed" && formData.natureOfWork && (
                    <div>
                      <span className="font-medium">Nature of Work:</span>{" "}
                      {formData.natureOfWork}
                    </div>
                  )}
                  {formData.employmentStatus === "employed" && formData.designation && (
                    <div>
                      <span className="font-medium">Designation:</span>{" "}
                      {formData.designation}
                    </div>
                  )}
                </div>
              </div>

              {/* Medical Information */}
              <div className="card">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <Stethoscope className="h-5 w-5 mr-2 text-primary-500" />
                  Medical Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Diagnosis:</span>{" "}
                    {formData.diagnosis}
                  </div>
                  <div>
                    <span className="font-medium">Treatment Type:</span>{" "}
                    {formData.treatmentType}
                  </div>
                  <div>
                    <span className="font-medium">Doctor:</span> Dr.{" "}
                    {formData.doctorName}
                  </div>
                  <div>
                    <span className="font-medium">Designation:</span>{" "}
                    {formData.doctorDesignation}
                  </div>
                  <div>
                    <span className="font-medium">Admission Date:</span>{" "}
                    {formData.admissionDate}
                  </div>
                  <div>
                    <span className="font-medium">Estimated Cost:</span> ₹
                    {parseFloat(formData.estimatedCost || 0).toLocaleString()}
                  </div>
                  <div>
                    <span className="font-medium">Treatment Plan:</span>{" "}
                    {formData.treatmentPlanned}
                  </div>
                  <div>
                    <span className="font-medium">Priority:</span>{" "}
                    {formData.priority}
                  </div>
                </div>
                {treatments.length > 0 && (
                  <div className="mt-4">
                    <span className="font-medium text-sm">
                      Additional Diagnoses ({treatments.length}):
                    </span>
                    <ul className="list-disc list-inside text-sm text-gray-600 mt-1">
                      {treatments.map((treatment) => (
                        <li key={treatment.id}>{treatment.diagnosisName}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Policy Holder Information */}
              <div className="card">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-primary-500" />
                  Policy Holder & Nominee
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Policy Holder:</span>{" "}
                    {formData.isSelf
                      ? "Self (Patient)"
                      : formData.policyHolderName}
                  </div>
                  {!formData.isSelf && (
                    <div>
                      <span className="font-medium">Relationship:</span>{" "}
                      {formData.relationshipWithPatient}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Policy Holder DOB:</span>{" "}
                    {formData.policyHolderDob || "-"}
                  </div>
                  <div>
                    <span className="font-medium">Alternate Mobile:</span>{" "}
                    {formData.policyHolderAlternatePhone || "-"}
                  </div>
                  <div>
                    <span className="font-medium">Nominee:</span>{" "}
                    {formData.nomineeName}
                  </div>
                  <div>
                    <span className="font-medium">Nominee Relationship:</span>{" "}
                    {formData.relationshipWithNominee}
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="card">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-primary-500" />
                  Documents Uploaded
                </h4>
                <div className="grid md:grid-cols-2 gap-2 text-sm">
                  {Object.entries(documents).map(([key, file]) => {
                    if (key === "treatmentDocs") {
                      return Object.entries(file || {}).map(
                        ([treatmentId, treatmentFile]) =>
                          treatmentFile && (
                            <div
                              key={treatmentId}
                              className="flex items-center space-x-2"
                            >
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span>Treatment Doc: {treatmentFile.name}</span>
                            </div>
                          )
                      );
                    }
                    if (key === "medicalHistoryDocs") {
                      return file.map((historyFile, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>Medical History: {historyFile.name}</span>
                        </div>
                      ));
                    }
                    return (
                      file && (
                        <div key={key} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>{file.name}</span>
                        </div>
                      )
                    );
                  })}
                </div>
              </div>

              {/* Additional Information */}
              {(formData.priority !== "normal" ||
                formData.emergency ||
                formData.remarks) && (
                  <div className="card">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                      <AlertCircle className="h-5 w-5 mr-2 text-primary-500" />
                      Additional Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      {formData.priority !== "normal" && (
                        <div>
                          <span className="font-medium">Priority:</span>{" "}
                          {formData.priority}
                        </div>
                      )}
                      {formData.emergency && (
                        <div className="flex items-center space-x-2">
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                          <span className="text-red-600 font-medium">
                            Emergency Case
                          </span>
                        </div>
                      )}
                      {formData.remarks && (
                        <div>
                          <span className="font-medium">Remarks:</span>{" "}
                          {formData.remarks}
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>

            {/* Validation Warnings for Super Admin */}
            {isSuperAdmin && showAdmissionDateOverride && (
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-800">Admin Override Active</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Date validation restrictions have been overridden for this application.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <p className="text-red-700">{errors.submit}</p>
                </div>
              </div>
            )}

            {/* Terms & Conditions */}
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800">
                    Terms & Conditions
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    By submitting this application, you confirm that all
                    information provided is accurate and complete. The
                    application will be processed according to the insurance
                    policy terms and hospital procedures.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            New Patient Application
          </h1>
          <p className="text-gray-600">
            Create a comprehensive insurance application for patient treatment
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {lastSaved && (
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to clear the saved draft and start fresh?')) {
                  localStorage.removeItem('newPatientApplication');
                  window.location.reload();
                }
              }}
              className="btn-outline text-sm text-red-600 border-red-300 hover:bg-red-50"
            >
              Clear Draft
            </button>
          )}
          <button onClick={onBack || (() => goBackToDashboard(navigate))} className="btn-outline">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Auto-save Indicator */}
      {lastSaved && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-sm text-green-800">
              Auto-saved at {lastSaved.toLocaleTimeString()}
            </span>
          </div>
        </div>
      )}

      {/* Progress Steps */}
      {!submitSuccess && (
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <div key={step.id} className="flex items-center">
                  {/* Step Circle */}
                  <div className="flex flex-col items-center">
                    <div
                      className={`flex items-center justify-center w-16 h-16 rounded-full border-2 transition-all duration-300 ${isActive
                          ? "bg-teal-500 border-teal-500 text-white shadow-lg"
                          : isCompleted
                            ? "bg-green-500 border-green-500 text-white"
                            : "bg-gray-50 border-gray-200 text-gray-400"
                        }`}
                    >
                      {isCompleted ? (
                        <CheckCircle className="h-7 w-7" />
                      ) : (
                        <Icon className="h-7 w-7" />
                      )}
                    </div>

                    {/* Step Label */}
                    <div className="mt-3 text-center max-w-24">
                      <p
                        className={`text-sm font-medium leading-tight ${isActive
                            ? "text-teal-600"
                            : isCompleted
                              ? "text-green-600"
                              : "text-gray-500"
                          }`}
                      >
                        {step.title}
                      </p>
                      <p
                        className={`text-xs mt-1 ${isActive
                            ? "text-teal-500"
                            : isCompleted
                              ? "text-green-500"
                              : "text-gray-400"
                          }`}
                      >
                        {step.description}
                      </p>
                    </div>
                  </div>

                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div
                      className={`h-0.5 w-16 mx-4 transition-colors duration-300 ${isCompleted ? "bg-green-500" : "bg-gray-200"
                        }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Form Content */}
      <div className="card-medical">{renderStepContent()}</div>

      {/* Navigation Buttons */}
      {!submitSuccess && (
        <div className="flex justify-between items-center">
          <button
            onClick={prevStep}
            disabled={currentStep === 1}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${currentStep === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
              }`}
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Previous</span>
          </button>

          {/* Step indicator */}
          <div className="text-sm text-gray-500 hidden md:block">
            Step {currentStep} of {steps.length}
          </div>

          {currentStep < 8 ? (
            <button
              onClick={nextStep}
              className="flex items-center space-x-2 btn-primary hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              <span>Next</span>
              <ArrowRight className="h-5 w-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center space-x-2 btn-success disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Save className="h-5 w-5" />
                  <span>Submit Application</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Help Section */}
      <div className="mt-8 text-center">
        <div className="bg-white/60 backdrop-blur-sm rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Need Help?
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Contact our support team if you need assistance with the application
            process
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6">
            <a
              href="mailto:support@samriddhihealthcare.com"
              className="flex items-center text-teal-600 hover:text-teal-700 transition-colors"
            >
              <Mail className="h-4 w-4 mr-2" />
              support@samriddhihealthcare.com
            </a>
            <a
              href="tel:+91-1800-XXX-XXXX"
              className="flex items-center text-teal-600 hover:text-teal-700 transition-colors"
            >
              <Phone className="h-4 w-4 mr-2" />
              +91 1800-XXX-XXXX
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewPatientApplicationLead;